import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tenantsAPI, auditAPI } from '../../services/api';
import { formatDate, formatDateTime, formatRelativeDate } from '../../utils/dateFormat';
import './Admin.css';

interface TenantInfo {
  id: number;
  name: string;
  subdomain: string | null;
  is_active: boolean;
  created_at: string;
}

interface TenantMember {
  id: string;
  name: string;
  email: string;
  role: string;
  user_type: string;
  is_active: boolean;
  last_login: string | null;
  last_seen: string | null;
  engagement: 'high' | 'medium' | 'low' | 'none';
  days_remaining: number | null;
}

interface AuditEntry {
  id: number;
  action: string;
  actor_name: string | null;
  actor_email: string | null;
  resource_type: string | null;
  resource_id: number | null;
  details: Record<string, any> | null;
  severity: string;
  event_category: string;
  created_at: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  info: '#68d391',
  warning: '#f6ad55',
  critical: '#fc8181',
};

const TenantDetailPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditTotalPages, setAuditTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const tid = parseInt(tenantId || '0', 10);

  const fetchData = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [tenantRes, membersRes] = await Promise.all([
        tenantsAPI.get(tid),
        tenantsAPI.getMembers(tid),
      ]);
      setTenant(tenantRes.data);
      setMembers(membersRes.data.members || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  }, [tid]);

  const fetchAudit = useCallback(async () => {
    if (!tid) return;
    setAuditLoading(true);
    try {
      const res = await auditAPI.getLog({
        page: auditPage,
        per_page: 20,
        tenant_id: tid,
      });
      setAuditEntries(res.data.entries || []);
      setAuditTotal(res.data.total || 0);
      setAuditTotalPages(res.data.total_pages || 0);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [tid, auditPage]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const formatAction = (action: string) =>
    action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const getRoleBadgeStyle = (role: string, userType: string): React.CSSProperties => {
    if (role === 'admin') return { color: '#f87171', background: 'rgba(248, 113, 113, 0.15)' };
    if (userType === 'guest') return { color: '#718096', background: 'rgba(113, 128, 150, 0.15)' };
    if (userType === 'trial_member') return { color: '#f6ad55', background: 'rgba(246, 173, 85, 0.15)' };
    return { color: '#68d391', background: 'rgba(104, 211, 145, 0.15)' };
  };

  const getRoleLabel = (role: string, userType: string): string => {
    if (role === 'admin') return 'admin';
    if (userType === 'trial_member') return 'trial';
    if (userType === 'guest') return 'guest';
    return 'member';
  };

  if (loading) {
    return <div className="loading-state"><div className="admin-loading-spinner" /></div>;
  }

  if (error || !tenant) {
    return (
      <div className="empty-state">
        <h3>{error || 'Tenant not found'}</h3>
        <button className="btn-secondary" onClick={() => navigate('/admin/tenants')}>Back to Tenants</button>
      </div>
    );
  }

  const admins = members.filter(m => m.role === 'admin');
  const regularMembers = members.filter(m => m.role !== 'admin' && m.user_type !== 'guest');
  const guests = members.filter(m => m.user_type === 'guest');

  return (
    <div className="admin-tenant-detail-page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button
          className="btn-secondary"
          onClick={() => navigate('/admin/tenants')}
          style={{ padding: '6px 10px', fontSize: '12px' }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
            <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          Back
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '20px', color: 'var(--text-primary)' }}>{tenant.name}</h1>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {tenant.subdomain ? `${tenant.subdomain}.adroitai.ai` : 'No subdomain'}
            {' \u00B7 '}Created {formatDate(tenant.created_at)}
          </div>
        </div>
        <span style={{
          fontSize: '12px', padding: '4px 10px', borderRadius: '6px',
          color: tenant.is_active ? '#68d391' : '#fc8181',
          background: tenant.is_active ? 'rgba(104,211,145,0.1)' : 'rgba(252,129,129,0.1)',
        }}>
          {tenant.is_active ? 'Active' : 'Suspended'}
        </span>
      </div>

      {/* Members Section */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '16px', color: 'var(--text-primary)', marginBottom: '12px' }}>
          Members ({members.length})
        </h2>
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Active</th>
                <th>Engagement</th>
              </tr>
            </thead>
            <tbody>
              {[...admins, ...regularMembers, ...guests].map((m) => (
                <tr key={m.id}>
                  <td style={{ color: 'var(--text-primary)', fontSize: '13px' }}>{m.name}</td>
                  <td className="table-cell-secondary" style={{ fontSize: '12px' }}>{m.email}</td>
                  <td>
                    <span style={{
                      ...getRoleBadgeStyle(m.role, m.user_type),
                      fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
                    }}>
                      {getRoleLabel(m.role, m.user_type)}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
                      color: m.is_active ? '#68d391' : '#fc8181',
                      background: m.is_active ? 'rgba(104,211,145,0.1)' : 'rgba(252,129,129,0.1)',
                    }}>
                      {m.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="table-cell-secondary" style={{ fontSize: '12px' }}>
                    {formatRelativeDate(m.last_seen || m.last_login)}
                  </td>
                  <td>
                    {m.engagement !== 'none' && (
                      <span style={{
                        fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
                        color: m.engagement === 'high' ? '#68d391' : m.engagement === 'medium' ? '#f6ad55' : '#fc8181',
                        background: m.engagement === 'high' ? 'rgba(104,211,145,0.1)' : m.engagement === 'medium' ? 'rgba(246,173,85,0.1)' : 'rgba(252,129,129,0.1)',
                      }}>
                        {m.engagement}
                      </span>
                    )}
                    {m.days_remaining !== null && (
                      <span style={{ fontSize: '10px', color: '#f6ad55', marginLeft: '6px' }}>
                        {m.days_remaining}d left
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>
                    No members in this tenant
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scoped Audit Log */}
      <div>
        <h2 style={{ fontSize: '16px', color: 'var(--text-primary)', marginBottom: '12px' }}>
          Audit Log ({auditTotal} events)
        </h2>
        <div className="admin-table-container">
          {auditLoading ? (
            <div className="loading-state"><div className="admin-loading-spinner" /></div>
          ) : auditEntries.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>
              No audit events for this tenant
            </div>
          ) : (
            <>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '30px' }}></th>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Resource</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        <div style={{
                          width: '8px', height: '8px', borderRadius: '50%',
                          background: SEVERITY_COLORS[entry.severity] || SEVERITY_COLORS.info,
                        }} />
                      </td>
                      <td className="table-cell-secondary" style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                        {formatDateTime(entry.created_at)}
                      </td>
                      <td style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                        {formatAction(entry.action)}
                      </td>
                      <td className="table-cell-secondary" style={{ fontSize: '12px' }}>
                        {entry.actor_name || entry.actor_email || 'System'}
                      </td>
                      <td className="table-cell-secondary" style={{ fontSize: '12px' }}>
                        {entry.resource_type || '-'}
                        {entry.resource_id ? ` #${entry.resource_id}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {auditTotalPages > 1 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Page {auditPage} of {auditTotalPages}
                  </div>
                  <div className="pagination-buttons">
                    <button className="pagination-btn" onClick={() => setAuditPage(p => p - 1)} disabled={auditPage === 1}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <button className="pagination-btn" onClick={() => setAuditPage(p => p + 1)} disabled={auditPage === auditTotalPages}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantDetailPage;
